import { ContentBlockGlobalNetwork } from '@dfh-corp-sites/ui'
import { GatsbyImage, withArtDirection } from 'gatsby-plugin-image'
import React, { useMemo } from 'react'

import { SanityContentBlockGlobalNetwork as SanityContentBlockGlobalNetworkProps } from '../../../graphql/gatsby'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { useSanityRoleBasedContent } from '../../../lib/sanity'
import { getGatsbyImageDataForAsset } from '../../../utils/sanity/image'
import SanityLink, { NormalizedSanityLinkTagged } from '../../global/SanityLink'

const setAnalyticsEventOnClick = (
  cta: SanityContentBlockGlobalNetworkProps['cta'],
  link: NormalizedSanityLinkTagged,
  title: SanityContentBlockGlobalNetworkProps['title'],
) => {
  setAnalyticsEvent({
    event: 'select_content',
    content_type: 'content block',
    item_id: `sanitycontentblock-sanitycontentglobalnetwork`,
    title: title || '',
    label_text: cta?.title || '',
    link_destination: link._tag === 'Normalized' ? link.href ?? undefined : link._tag,
    module_name: 'SanityContentBlock',
    module_variant: 'SanityContentBlockGlobalNetwork',
  })
}

const SanityContentBlockGlobalNetwork = ({
  title,
  heading,
  primaryText,
  secondaryText,
  cta,
  ctaAriaLabel,
  image,
  imageAlt,
  roleBasedContent,
}: SanityContentBlockGlobalNetworkProps) => {
  const { shouldDisplay } = useSanityRoleBasedContent({ roleBasedContent })

  const imageElement = useMemo((): JSX.Element | undefined => {
    const mobileImageData = getGatsbyImageDataForAsset(image, {
      width: 327,
      height: 203,
      placeholder: 'blurred',
    })
    const tabletImageData = getGatsbyImageDataForAsset(image, {
      width: 786,
      height: 430,
      placeholder: 'blurred',
    })

    const images =
      mobileImageData && tabletImageData
        ? withArtDirection(tabletImageData, [
            {
              image: mobileImageData,
              media: '(max-width: 576px)',
            },
            {
              image: tabletImageData,
              media: '(max-width: 768px)',
            },
            {
              image: tabletImageData,
              media: '(min-width: 1024px)',
            },
          ])
        : tabletImageData

    return images ? (
      <div className="relative">
        <GatsbyImage image={images} loading="eager" alt={imageAlt || ''} />
      </div>
    ) : undefined
  }, [image, imageAlt])

  const ctaChildren =
    cta?.link && cta.link.target && cta.link.target.length > 0 ? (
      <SanityLink
        to={cta.link}
        onClick={(_e, normalized) => setAnalyticsEventOnClick(cta, normalized, title)}
      >
        {cta.title}
      </SanityLink>
    ) : null

  if (!shouldDisplay) return null
  return (
    <ContentBlockGlobalNetwork
      title={title || ''}
      heading={heading || ''}
      primaryText={primaryText || ''}
      secondaryText={secondaryText || ''}
      ctaChildren={ctaChildren}
      ctaAriaLabel={ctaAriaLabel || ''}
      image={imageElement}
    />
  )
}
export default SanityContentBlockGlobalNetwork
